import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, ArrowRight, Loader2, Star } from 'lucide-react';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { ptDate, addDays } from '@/utils/pioneerDates';

export default function TrialAccept() {
  const { token } = useParams();
  const navigate    = useNavigate();
  const { user }    = useAuth();

  const [offer,    setOffer]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [done,     setDone]     = useState(null);
  const [error,    setError]    = useState('');
  const [consent,  setConsent]  = useState(false);
  const [logo,     setLogo]     = useState(null);
  const logoPreview = React.useMemo(() => (logo ? URL.createObjectURL(logo) : ''), [logo]);
  useEffect(() => () => { if (logoPreview) URL.revokeObjectURL(logoPreview); }, [logoPreview]);

  useEffect(() => {
    api.get(`/trial/offer/${token}`)
      .then(r => setOffer(r.data))
      .catch(() => setError('Link inválido ou expirado.'))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('consent', consent ? 'true' : 'false');
      if (logo) fd.append('logo', logo);
      const res = await api.post(`/trial/offer/${token}/accept`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDone(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Erro ao activar o trial.';
      if (detail.includes('Conta não encontrada')) {
        // Redirecionar para registo com email pré-preenchido
        const email = offer?.email || '';
        navigate(`/start?email=${encodeURIComponent(email)}&trial=${token}`);
      } else {
        setError(detail);
      }
    } finally {
      setAccepting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
    </div>
  );

  if (error && !offer) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="text-center max-w-sm">
        <XCircle className="h-16 w-16 text-rose-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Link inválido</h1>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  if (offer?.status === 'accepted' || done) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="text-center max-w-md bg-white rounded-2xl shadow-lg p-10 border border-emerald-100">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="h-10 w-10 text-emerald-600" />
        </div>
        <h1 className="text-3xl font-black text-gray-900 mb-3">Trial Activado!</h1>
        <p className="text-gray-500 mb-2 text-lg">
          <strong>{offer?.company_name}</strong> tem agora
        </p>
        <div className="bg-gradient-to-r from-blue-50 to-emerald-50 border border-emerald-200 rounded-xl p-6 my-6">
          <div className="text-5xl font-black text-blue-800 mb-1">60 dias</div>
          <div className="text-emerald-700 font-semibold">de publicações gratuitas</div>
          <div className="text-gray-400 text-sm mt-1">Vagas ilimitadas · Badge Verificado</div>
          {(done?.trial_ends_at || offer?.trial_ends_at) && (
            <div className="text-sm text-blue-900 mt-3" data-testid="trial-accepted-dates">
              Válido de <strong>{ptDate(done?.trial_started_at || offer?.trial_started_at)}</strong> até <strong>{ptDate(done?.trial_ends_at || offer?.trial_ends_at)}</strong>
            </div>
          )}
        </div>
        <p className="text-gray-500 mb-8 text-sm">Verificaste o teu email? Enviámos uma confirmação com todos os detalhes.</p>
        <button
          onClick={() => navigate(user ? '/create-job' : '/login')}
          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-700 to-violet-700 text-white font-bold py-4 rounded-xl text-lg hover:opacity-90 transition"
          data-testid="trial-accept-cta"
        >
          {user ? 'Publicar primeira vaga' : 'Entrar na minha conta'}
          <ArrowRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
      {/* Logo */}
      <div className="flex items-center gap-3 mb-10">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-violet-700 flex items-center justify-center text-white font-black text-xl">T</div>
        <span className="text-2xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Trabadju</span>
        <span className="text-[8px] font-bold tracking-[.22em] text-slate-500 uppercase mt-0.5 text-center block">Cabo Verde</span>
      </div>

      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-800 to-violet-700 px-8 py-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            <Star className="h-3.5 w-3.5" />
            Oferta Exclusiva Pioneiro
          </div>
          <h1 className="text-3xl font-black text-white leading-tight">
            {offer?.company_name}
          </h1>
          <p className="text-white/80 mt-2">A vossa oferta especial está à vossa espera</p>
        </div>

        {/* Body */}
        <div className="px-8 py-8">
          {/* Offer box */}
          <div className="bg-gradient-to-br from-blue-50 to-emerald-50 border-2 border-blue-200 rounded-xl p-6 mb-6 text-center">
            <div className="text-6xl font-black text-blue-800 leading-none">60</div>
            <div className="text-xl font-bold text-gray-700 mt-1">dias gratuitos</div>
            <div className="text-sm text-gray-500 mt-1">para publicar vagas no Trabadju</div>
            <div className="text-sm text-blue-900 font-semibold mt-3" data-testid="trial-start-info">
              Os 60 dias começam hoje ({ptDate(new Date())}) e terminam a {ptDate(addDays(new Date(), 60))}.
            </div>
          </div>

          {/* Benefits */}
          <ul className="space-y-3 mb-8">
            {[
              'Vagas ilimitadas durante 60 dias',
              'Badge "Empresa Verificada" nas vossas vagas',
              'Acesso a todos os candidatos recebidos',
              'Ferramentas de recrutamento com IA incluídas',
            ].map((b) => (
              <li key={b} className="flex items-center gap-3 text-gray-700">
                <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          {/* Auth note */}
          {!user && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 flex items-start gap-3">
              <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800 text-sm">
                Para activar o trial, precisamos de associar esta oferta à vossa conta Trabadju.
                Se ainda não têm conta, criamos uma em 2 minutos.
              </p>
            </div>
          )}

          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 mb-4 text-rose-700 text-sm">
              {error}
            </div>
          )}

          {/* Mural de Confiança */}
          <div className="border border-slate-200 rounded-xl p-4 mb-6 space-y-3" data-testid="pioneer-wall-consent-box">
            <div>
              <label htmlFor="pioneer-logo" className="block text-xs font-semibold text-gray-600 mb-1">Logótipo da empresa (PNG ou SVG · também JPG/WEBP · máx. 2 MB) — opcional</label>
              <input id="pioneer-logo" type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" onChange={(e) => setLogo(e.target.files?.[0] || null)} className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 file:font-semibold" data-testid="pioneer-logo-input" />
              {logo && <img src={logoPreview} alt="" className="mt-2 h-14 object-contain" data-testid="pioneer-logo-preview" />}
              {!logo && <p className="mt-1 text-[11px] text-gray-500">Se não carregar, procuramos o logótipo no site da empresa e pedimos a aprovação da equipa Trabadju antes de o mostrar.</p>}
            </div>
            <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-700" data-testid="pioneer-consent-checkbox" />
              <span>Autorizo o Trabadju.cv a mostrar o nosso logo no <strong>Mural de Confiança</strong>.</span>
            </label>
            <p className="text-[11px] text-gray-500">Opcional — pode aceitar a oferta sem autorizar. O logo só aparece no site depois de aprovado pela equipa Trabadju.</p>
          </div>

          {/* CTA */}
          <button
            onClick={accept}
            disabled={accepting}
            className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-blue-700 to-violet-700 text-white font-black py-5 px-4 rounded-xl text-base sm:text-lg text-center hover:opacity-90 transition disabled:opacity-60"
            data-testid="trial-accept-btn"
          >
            {accepting ? (
              <><Loader2 className="h-5 w-5 animate-spin" /> A activar…</>
            ) : (
              <><CheckCircle className="h-5 w-5" /> Aceitar oferta de 60 dias</>
            )}
          </button>

          <p className="text-xs text-gray-400 text-center mt-3">
            Oferta válida por 30 dias · Exclusiva para {offer?.company_name}
          </p>
        </div>
      </div>
    </div>
  );
}
